import { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '@/config/api';
import { applySensitivityFromBaseline, shouldInvalidateBaselineField } from '@/utils/quickTestSensitivity';
import { nextAutoTarget } from '@/utils/quickTestTargets';
import { parseTargetChf } from '@/utils/targetChfInput';
import { isEffectivelyZero } from '@/utils/clearableNumber';

const API = API_BASE_URL;

const EN_DASH = '\u2013';

export function QuickTest({ inputs, onInputChange, cadastreData, marketData, assumptions, roiTargets, refreshKey }) {
  const tests = [
    { label: 'Price +10%', field: 'pricePerSqm', factor: 1.1, tid: 'price-up' },
    { label: 'Price -10%', field: 'pricePerSqm', factor: 0.9, tid: 'price-down' },
    { label: 'Land +10%', field: 'landPrice', factor: 1.1, tid: 'land-up' },
    { label: 'Land -10%', field: 'landPrice', factor: 0.9, tid: 'land-down' },
  ];
  const fmt = (v) => (v ? Math.round(v).toLocaleString('de-CH') : EN_DASH);
  const opCur = (assumptions?.operatingCurrency || 'CHF').toUpperCase();
  const [targetLandError, setTargetLandError] = useState('');
  const [targetPriceError, setTargetPriceError] = useState('');
  // QA-019: local drafts so invalid keystrokes remain visible (controlled value must not drop them)
  const [landDraft, setLandDraft] = useState(() => (inputs.targetLandPrice ? Math.round(inputs.targetLandPrice).toLocaleString('de-CH') : ''));
  const [priceDraft, setPriceDraft] = useState(() => (inputs.targetPricePerSqm ? Math.round(inputs.targetPricePerSqm).toLocaleString('de-CH') : ''));
  const landEditingRef = useRef(false);
  const priceEditingRef = useRef(false);
  // Manual edit locks — auto-sync resumes after land data refresh.
  const landEditedRef = useRef(false);
  const priceEditedRef = useRef(false);
  const lastSyncedLandRef = useRef(null);
  const lastSyncedPriceRef = useRef(null);
  const lastRefreshKey = useRef(refreshKey);
  if (refreshKey !== lastRefreshKey.current) {
    lastRefreshKey.current = refreshKey;
    landEditedRef.current = false;
    priceEditedRef.current = false;
    lastSyncedLandRef.current = null;
    lastSyncedPriceRef.current = null;
  }

  useEffect(() => {
    if (landEditingRef.current) return;
    setLandDraft(inputs.targetLandPrice ? Math.round(inputs.targetLandPrice).toLocaleString('de-CH') : '');
    setTargetLandError('');
  }, [inputs.targetLandPrice]);

  useEffect(() => {
    if (priceEditingRef.current) return;
    setPriceDraft(inputs.targetPricePerSqm ? Math.round(inputs.targetPricePerSqm).toLocaleString('de-CH') : '');
    setTargetPriceError('');
  }, [inputs.targetPricePerSqm]);

  const onTargetDraftChange = (raw, { setDraft, setError, field }) => {
    setDraft(raw);
    const { value, error } = parseTargetChf(raw);
    setError(error);
    if (value === null) return;
    if (field === 'targetLandPrice') landEditedRef.current = true;
    if (field === 'targetPricePerSqm') priceEditedRef.current = true;
    onInputChange((prev) => ({ ...prev, [field]: value }));
  };

  // QA-003: apply ±% against a frozen baseline so opposite tests don't compound
  const baselineRef = useRef(null);
  const [activeFactors, setActiveFactors] = useState({});
  const [hasBaseline, setHasBaseline] = useState(false);
  useEffect(() => {
    baselineRef.current = null;
    setActiveFactors({});
    setHasBaseline(false);
  }, [refreshKey]);

  // Invalidate sticky baseline when the user (or Apply) changes price/land outside the active test value
  useEffect(() => {
    if (!baselineRef.current) return;
    const baseline = baselineRef.current;
    let nextFactors = { ...activeFactors };
    let changed = false;
    for (const field of ['pricePerSqm', 'landPrice']) {
      const factor = nextFactors[field];
      if (shouldInvalidateBaselineField(baseline, field, factor ?? null, inputs[field])) {
        if (factor == null) {
          baselineRef.current = null;
          setActiveFactors({});
          setHasBaseline(false);
          return;
        }
        delete nextFactors[field];
        changed = true;
      }
    }
    if (changed) {
      if (Object.keys(nextFactors).length === 0) {
        baselineRef.current = null;
        setActiveFactors({});
        setHasBaseline(false);
      } else {
        setActiveFactors(nextFactors);
      }
    }
  }, [inputs.pricePerSqm, inputs.landPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  const ensureBaseline = () => {
    if (!baselineRef.current) {
      baselineRef.current = {
        pricePerSqm: inputs.pricePerSqm,
        landPrice: inputs.landPrice,
      };
      setHasBaseline(true);
    }
    return baselineRef.current;
  };

  const applySensitivity = (field, factor) => {
    const baseline = ensureBaseline();
    const nextFactors = { ...activeFactors, [field]: factor };
    // Toggle off if clicking the same active test again → restore baseline for that field
    if (activeFactors[field] === factor) {
      delete nextFactors[field];
      setActiveFactors(nextFactors);
      onInputChange((prev) => ({ ...prev, [field]: baseline[field] }));
      if (Object.keys(nextFactors).length === 0) {
        baselineRef.current = null;
        setHasBaseline(false);
      }
      return;
    }
    setActiveFactors(nextFactors);
    onInputChange((prev) => ({
      ...prev,
      [field]: applySensitivityFromBaseline(baseline, field, factor),
    }));
  };

  const resetSensitivity = () => {
    if (!baselineRef.current) return;
    const baseline = baselineRef.current;
    onInputChange((prev) => ({
      ...prev,
      pricePerSqm: baseline.pricePerSqm,
      landPrice: baseline.landPrice,
    }));
    baselineRef.current = null;
    setActiveFactors({});
    setHasBaseline(false);
  };

  // Surface from cadastre or inputs
  const surface = cadastreData?.surface_m2 || inputs.landSize || 0;
  // Market bands are in quote_currency; only feed QuickTest when that matches ops currency.
  const marketQuote = (marketData?.quote_currency || '').toUpperCase();
  const marketCountry = (marketData?.country_code || '').toUpperCase();
  const marketCurrencyUsable = marketData?.status === 'ok' && (
    (marketQuote && marketQuote === opCur)
    || (!marketQuote && !marketCountry && opCur === 'CHF') // legacy Swiss cache
  );
  const marketPrice = marketCurrencyUsable ? marketData.price_per_sqm : null;

  // ROI-based targets from backend (most useful reference)
  const roiLand = roiTargets.find(t => t.field === 'landPrice');
  const roiPrice = roiTargets.find(t => t.field === 'pricePerSqm');

  // Market-based reference from backend (fallback — single source of truth)
  // Keep these aligned with the core deal model keys used by calculation_engine.py.
  const CUS = inputs.buildabilityCoefficient || 0.5;
  const netRatio = inputs.netSaleablePercent || 0.93;
  const [marketRef, setMarketRef] = useState(null);
  useEffect(() => {
    if (marketPrice && surface > 0) {
      fetch(`${API}/api/market-reference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ market_price_per_sqm: marketPrice, surface, CUS, netSaleableRatio: netRatio }),
      })
        .then(r => r.json())
        .then(data => setMarketRef(data))
        .catch(() => setMarketRef(null));
    } else {
      setMarketRef(null);
    }
  }, [marketPrice, surface, CUS, netRatio]);

  const marketRefLand = marketRef?.marketRefLandPrice || null;
  const marketRefPrice = marketRef?.marketRefPricePerSqm || null;

  // Best reference: ROI target > market estimate
  const refLandPrice = roiLand?.targetValue ? Math.round(roiLand.targetValue) : marketRefLand;
  const refLandLabel = roiLand ? roiLand.label : (marketRefLand ? 'market' : null);
  const refPricePerSqm = roiPrice?.targetValue ? Math.round(roiPrice.targetValue) : marketRefPrice;
  const refPriceLabel = roiPrice ? roiPrice.label : (marketRefPrice ? 'market' : null);

  // Stored values
  const targetLand = inputs.targetLandPrice || 0;
  const targetPrice = inputs.targetPricePerSqm || 0;

  // Keep target inputs synced to live ROI/market refs until the user edits them.
  // Previous bug: one-shot autoPop left a stale target (e.g. 845k) while the
  // "80% ROI ref" line kept updating (e.g. 3.99M) after price/land changes.
  useEffect(() => {
    const land = nextAutoTarget({
      refValue: refLandPrice,
      currentTarget: inputs.targetLandPrice || 0,
      userEdited: landEditedRef.current,
      lastSynced: lastSyncedLandRef.current,
    });
    const price = nextAutoTarget({
      refValue: refPricePerSqm,
      currentTarget: inputs.targetPricePerSqm || 0,
      userEdited: priceEditedRef.current,
      lastSynced: lastSyncedPriceRef.current,
    });
    const updates = {};
    if (land.shouldUpdate) {
      updates.targetLandPrice = land.nextTarget;
      lastSyncedLandRef.current = land.nextLastSynced;
      landEditedRef.current = false;
    }
    if (price.shouldUpdate) {
      updates.targetPricePerSqm = price.nextTarget;
      lastSyncedPriceRef.current = price.nextLastSynced;
      priceEditedRef.current = false;
    }
    if (Object.keys(updates).length > 0) {
      onInputChange((prev) => ({ ...prev, ...updates }));
    }
  }, [refLandPrice, refPricePerSqm, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deltas vs current
  const landDelta = targetLand && inputs.landPrice ? ((targetLand - inputs.landPrice) / inputs.landPrice * 100).toFixed(0) : null;
  const priceDelta = targetPrice && inputs.pricePerSqm ? ((targetPrice - inputs.pricePerSqm) / inputs.pricePerSqm * 100).toFixed(0) : null;

  return (
    <div data-testid="quick-test-section">
      <div className="flex items-center justify-between mb-1.5 md:mb-1">
        <div className="text-[11px] md:text-[8px] font-bold text-slate-400 uppercase tracking-wide">Quick Test</div>
        {hasBaseline && (
          <button
            type="button"
            onClick={resetSensitivity}
            className="text-[9px] md:text-[7px] font-semibold text-indigo-600 hover:text-indigo-800 min-h-[24px] px-1.5"
            data-testid="sensitivity-reset"
          >Reset</button>
        )}
      </div>
      <div className="text-[8px] md:text-[6px] text-slate-400 mb-1.5 md:mb-1">±% vs baseline (not compounded)</div>
      <div className="grid grid-cols-4 gap-1.5 md:gap-1 mb-2.5 md:mb-2">
        {tests.map(s => {
          const active = activeFactors[s.field] === s.factor;
          return (
          <button key={s.tid}
            type="button"
            onClick={() => applySensitivity(s.field, s.factor)}
            className={`text-[10px] md:text-[8px] px-2 py-2 md:py-1.5 min-h-[32px] md:min-h-[28px] border rounded transition-colors ${
              active
                ? 'bg-indigo-100 border-indigo-400 text-indigo-800 font-semibold'
                : 'bg-slate-50 border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 active:bg-indigo-100 text-slate-600'
            }`}
            data-testid={`sensitivity-${s.tid}`}
            aria-pressed={active}
          >{s.label}</button>
          );
        })}
      </div>

      {/* Target References */}
      <div className="space-y-2 md:space-y-1.5" data-testid="target-references">
        {/* Target Land Price */}
        <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg px-3 md:px-2.5 py-2.5 md:py-2" data-testid="target-land-price-row">
          <div className="flex items-center justify-between mb-1.5 md:mb-1">
            <div className="flex items-center gap-1">
              <span className="text-[11px] md:text-[8px] font-bold text-indigo-700">Target land price</span>
              {surface > 0 && <span className="text-[9px] md:text-[7px] text-indigo-400">for {fmt(surface)} m²</span>}
            </div>
            <button
              onClick={() => onInputChange((prev) => ({ ...prev, landPrice: targetLand }))}
              disabled={!targetLand || Boolean(targetLandError)}
              className="text-[10px] md:text-[8px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-30 px-3 md:px-2 py-1.5 md:py-1 min-h-[28px] rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              data-testid="target-land-apply"
            >Apply</button>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5 flex-1">
              <span className="text-[9px] md:text-[7px] text-indigo-400">{opCur}</span>
              <input
                type="text"
                inputMode="numeric"
                value={landDraft}
                onFocus={() => {
                  landEditingRef.current = true;
                  if (isEffectivelyZero(String(landDraft).replace(/['\s]/g, ''))) setLandDraft('');
                }}
                onBlur={() => {
                  landEditingRef.current = false;
                  setLandDraft(inputs.targetLandPrice ? Math.round(inputs.targetLandPrice).toLocaleString('de-CH') : '');
                  if (targetLandError) setTargetLandError('');
                }}
                onChange={(e) => onTargetDraftChange(e.target.value, {
                  setDraft: setLandDraft,
                  setError: setTargetLandError,
                  field: 'targetLandPrice',
                })}
                placeholder={EN_DASH}
                aria-label={`Target land price in ${opCur}`}
                aria-invalid={Boolean(targetLandError)}
                className="w-full text-[14px] md:text-[10px] font-bold text-indigo-800 bg-transparent border-none outline-none focus-visible:ring-1 focus-visible:ring-indigo-400 rounded tabular-nums placeholder:text-indigo-300 h-[32px] md:h-auto"
                data-testid="target-land-input"
              />
            </div>
            {landDelta && (
              <span className={`text-[9px] md:text-[7px] font-semibold tabular-nums shrink-0 ${Number(landDelta) <= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {Number(landDelta) > 0 ? '+' : ''}{landDelta}% vs current
              </span>
            )}
          </div>
          {targetLandError && (
            <div className="text-[8px] text-red-600 mt-0.5" data-testid="target-land-error">{targetLandError}</div>
          )}
          {refLandPrice && !targetLand && (
            <button
              onClick={() => {
                landEditedRef.current = false;
                lastSyncedLandRef.current = refLandPrice;
                onInputChange((prev) => ({ ...prev, targetLandPrice: refLandPrice }));
              }}
              className="mt-1 text-[9px] md:text-[7px] text-indigo-500 hover:text-indigo-700 underline underline-offset-2 py-1 md:py-0"
              data-testid="target-land-suggest"
            >Set from {refLandLabel}: {opCur} {fmt(refLandPrice)}</button>
          )}
          {refLandPrice && targetLand > 0 && (
            <div className="text-[9px] md:text-[7px] text-indigo-400 mt-0.5">{refLandLabel} ref: {opCur} {fmt(refLandPrice)}</div>
          )}
        </div>

        {/* Target Price Per Sqm */}
        <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg px-3 md:px-2.5 py-2.5 md:py-2" data-testid="target-price-sqm-row">
          <div className="flex items-center justify-between mb-1.5 md:mb-1">
            <div className="flex items-center gap-1">
              <span className="text-[11px] md:text-[8px] font-bold text-indigo-700">Target {opCur}/m²</span>
              {surface > 0 && <span className="text-[9px] md:text-[7px] text-indigo-400">for {fmt(surface)} m²</span>}
            </div>
            <button
              onClick={() => onInputChange((prev) => ({ ...prev, pricePerSqm: targetPrice }))}
              disabled={!targetPrice || Boolean(targetPriceError)}
              className="text-[10px] md:text-[8px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-30 px-3 md:px-2 py-1.5 md:py-1 min-h-[28px] rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              data-testid="target-price-apply"
            >Apply</button>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5 flex-1">
              <span className="text-[9px] md:text-[7px] text-indigo-400">{opCur}</span>
              <input
                type="text"
                inputMode="numeric"
                value={priceDraft}
                onFocus={() => {
                  priceEditingRef.current = true;
                  if (isEffectivelyZero(String(priceDraft).replace(/['\s]/g, ''))) setPriceDraft('');
                }}
                onBlur={() => {
                  priceEditingRef.current = false;
                  setPriceDraft(inputs.targetPricePerSqm ? Math.round(inputs.targetPricePerSqm).toLocaleString('de-CH') : '');
                  if (targetPriceError) setTargetPriceError('');
                }}
                onChange={(e) => onTargetDraftChange(e.target.value, {
                  setDraft: setPriceDraft,
                  setError: setTargetPriceError,
                  field: 'targetPricePerSqm',
                })}
                placeholder={EN_DASH}
                aria-label={`Target price per square meter in ${opCur}`}
                aria-invalid={Boolean(targetPriceError)}
                className="w-full text-[14px] md:text-[10px] font-bold text-indigo-800 bg-transparent border-none outline-none focus-visible:ring-1 focus-visible:ring-indigo-400 rounded tabular-nums placeholder:text-indigo-300 h-[32px] md:h-auto"
                data-testid="target-price-input"
              />
            </div>
            {priceDelta && (
              <span className={`text-[9px] md:text-[7px] font-semibold tabular-nums shrink-0 ${Number(priceDelta) <= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {Number(priceDelta) > 0 ? '+' : ''}{priceDelta}% vs current
              </span>
            )}
          </div>
          {targetPriceError && (
            <div className="text-[8px] text-red-600 mt-0.5" data-testid="target-price-error">{targetPriceError}</div>
          )}
          {refPricePerSqm && !targetPrice && (
            <button
              onClick={() => {
                priceEditedRef.current = false;
                lastSyncedPriceRef.current = refPricePerSqm;
                onInputChange((prev) => ({ ...prev, targetPricePerSqm: refPricePerSqm }));
              }}
              className="mt-1 text-[9px] md:text-[7px] text-indigo-500 hover:text-indigo-700 underline underline-offset-2 py-1 md:py-0"
              data-testid="target-price-suggest"
            >Set from {refPriceLabel}: {opCur} {fmt(refPricePerSqm)}</button>
          )}
          {refPricePerSqm && targetPrice > 0 && (
            <div className="text-[9px] md:text-[7px] text-indigo-400 mt-0.5">{refPriceLabel} ref: {opCur} {fmt(refPricePerSqm)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
